import React from "react";
import PropTypes from "prop-types";
import { format, isValid } from "date-fns";

import styles from "./TajmahalPayslip.module.css";

/* ---------- helpers ---------- */
const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isValid(d) ? d : null;
};

const fmtDate = (value, pattern) => {
  const d = toDate(value);
  return d ? format(d, pattern) : "";
};

const money = (value) =>
  `$${Number(value || 0).toLocaleString("en-SG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Shows "NA" when the value is empty or zero (e.g. CPF for foreign workers)
const moneyOrNA = (value) =>
  value === null || value === undefined || Number(value) === 0
    ? "NA"
    : money(value);

// Sum amounts from a list whose description matches any of the patterns
const pickAmount = (list = [], patterns = []) =>
  list
    .filter((item) =>
      patterns.some((p) => p.test(String(item?.description || ""))),
    )
    .reduce((sum, item) => sum + Number(item?.amount || 0), 0);

/* ---------- component ---------- */
const TajmahalPayslip = ({ payrollDetails = {}, companyDetails = {} }) => {
  const earnings = payrollDetails?.earningsList || [];
  const deductions = payrollDetails?.deductionsList || [];

  const company = {
    ...companyDetails,
    addressLines: [
      companyDetails?.address1,
      companyDetails?.address2,
      companyDetails?.address3,
      `${companyDetails?.country} - ${companyDetails?.zipCode}`,
    ],
  };

  const basicSalary =
    payrollDetails?.basicSalary ??
    pickAmount(earnings, [/basic/i, /^salary$/i]);
  const allowances =
    payrollDetails?.allowanceTotal ?? pickAmount(earnings, [/allowance/i]);
  const ot15 =
    payrollDetails?.ot1_5Amount ??
    pickAmount(earnings, [/ot.*1\.5/i, /over\s*time.*1\.5/i]) ??
    0;
  const ot2 =
    payrollDetails?.ot2Amount ??
    pickAmount(earnings, [/ot.*x?\s*2(?!\d)/i, /over\s*time.*x?\s*2(?!\d)/i]);
  const commission =
    payrollDetails?.commission ?? pickAmount(earnings, [/commi/i]);

  const cpfEmployer = payrollDetails?.employerCpf;
  const cpfEmployee =
    payrollDetails?.employeeCpf ?? pickAmount(deductions, [/cpf/i]);
  const agencyFee =
    payrollDetails?.agencyFee ?? pickAmount(deductions, [/agency/i]);
  const advanceLoan =
    payrollDetails?.advanceLoan ??
    pickAmount(deductions, [/advance/i, /loan/i]);

  const fromDate = fmtDate(payrollDetails?.fromDate, "do MMMM yyyy");
  const toDateStr = fmtDate(payrollDetails?.toDate, "do MMMM yyyy");
  const payDate = fmtDate(payrollDetails?.paymentDate, "d-MMM-yy");
  const finNo = payrollDetails?.finNumber || payrollDetails?.icno || "";

  const ALWAYS_SHOW = ["Incentive Pay", "Other Allowance"];

  const visibleEarnings = (payrollDetails?.earningsList || []).filter(
    (pd) => Number(pd?.amount) !== 0 || ALWAYS_SHOW.includes(pd?.description),
  );

  // Right column needs at least 3 rows: Payment Method, Basic Salary, Authorised By
  const earningRowCount = Math.max(visibleEarnings.length, 3);
  const authorisedRowSpan = earningRowCount - 2;

  return (
    <div className={styles.page}>
      {/* ---------- header ---------- */}
      <header className={styles.header}>
        <div className={styles.companyInfo}>
          <p>{company.companyName}</p>
          {company.regNo && <p>Company Reg:{company.regNo}</p>}
          {company.addressLines.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
          {(company.phoneNo || company.faxNo) && (
            <p>
              {company.phoneNo && `Tel: ${company.phoneNo}`}
              {company.phoneNo && company.faxNo && " | "}
              {company.faxNo && `Fax: ${company.faxNo}`}
            </p>
          )}
          {company.email && <p>E-Mail: {company.email}</p>}
        </div>

        {companyDetails?.companyLogo && (
          <img
            className={styles.logo}
            src={`data:image/png;base64,${companyDetails.companyLogo}`}
            alt="Company Logo"
          />
        )}
      </header>

      {/* ---------- main grid ---------- */}
      <table className={styles.grid}>
        <colgroup>
          <col className={styles.colLabel} />
          <col className={styles.colValue} />
          <col className={styles.colRightLabel} />
          <col className={styles.colRightValue} />
        </colgroup>
        <tbody>
          {/* top block – no inner divider on the left */}
          <tr>
            <td className={styles.noRightBorder}>Employee Name:</td>
            <td className={`${styles.center} ${styles.leftEdge}`}>
              {payrollDetails?.fullName}
            </td>
            <td className={styles.noRightBorder}>Paid for the Month :</td>
            <td className={styles.right}>
              {fromDate} - {toDateStr}
            </td>
          </tr>
          <tr>
            <td className={styles.noRightBorder}>NRIC/FIN :</td>
            <td className={`${styles.center} ${styles.leftEdge}`}>{finNo}</td>
            <td className={styles.noRightBorder}>Pay Date :</td>
            <td className={styles.right}>{payDate}</td>
          </tr>
          {Array.from({ length: earningRowCount }).map((_, i) => {
            const pd = visibleEarnings[i];

            return (
              <tr key={pd ? `${pd.description}-${i}` : `pad-${i}`}>
                {/* left side: earning row, or an empty row as padding */}
                <td>{pd ? pd.description : "\u00A0"}</td>
                <td className={styles.center}>{pd ? money(pd.amount) : ""}</td>

                {/* right side */}
                {i === 0 && (
                  <>
                    <td className={styles.noRightBorder}>Payment Method:</td>
                    <td className={styles.right}>
                      {payrollDetails?.paymentMode}
                    </td>
                  </>
                )}
                {i === 1 && (
                  <>
                    <td className={styles.noRightBorder}>Basic Salary:</td>
                    <td className={styles.right}>
                      {money(payrollDetails?.basicSalary)}
                    </td>
                  </>
                )}
                {i === 2 && (
                  <td
                    colSpan={2}
                    rowSpan={authorisedRowSpan}
                    className={styles.authorised}
                  >
                    <div>Authorised By:</div>
                    <div className={styles.authorisedName}>
                      {payrollDetails?.approvedBy}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
          {/* {payrollDetails?.earningsList.map(
            (pd, _k) =>
              (Number(pd?.amount) !== Number(0) || pd?.description == "Incentive Pay" || pd?.description == "Other Allowance" )&& (
                <tr key={_k}>
                  <td className="">{pd?.description}</td>
                  <td className={`${styles.center} ${styles.leftEdge}`}>
                    {money(pd?.amount)}
                  </td>
                  {_k === 0 && (
                    <>
                      <td className="">Payment Method:</td>
                      <td className={styles.right}>
                        {payrollDetails?.paymentMode}
                      </td>
                    </>
                  )}
                  {_k === 1 && (
                    <>
                      <td className="">Basic Salary:</td>
                      <td className={styles.right}>
                        {money(payrollDetails?.basicSalary)}
                      </td>
                    </>
                  )}
                  {_k === 2 && (
                    <>
                      <td colSpan={2} rowSpan={3} className={styles.authorised}>
                        <div>Authorised By:</div>
                        <div className={styles.authorisedName}>
                          {payrollDetails?.approvedBy}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
                // <div className={styles.payrollListRow} key={_k}>
                //   <span className={styles.label}>{pd?.description}</span>
                //   <span className={styles.value}>
                //     {Number(pd?.amount).toFixed(2)}
                //   </span>
                // </div>
              ),
          )} */}
          {/* <tr>
            <td className={styles.noRightBorder}>Salary</td>
            <td className={`${styles.center} ${styles.leftEdge}`}>
              {money(basicSalary)}
            </td>
            <td className={styles.noRightBorder}>Payment Method:</td>
            <td className={styles.right}>{payrollDetails?.paymentMode}</td>
          </tr> */}

          {/* earnings */}
          {/* <tr>
            <td>Allowances</td>
            <td className={styles.center}>{money(allowances)}</td>
            <td className={styles.noRightBorder}>Basic Salary:</td>
            <td className={styles.right}>{money(basicSalary)}</td>
          </tr>
          <tr>
            <td>Over time(x1.5)</td>
            <td className={styles.center}>{money(ot15)}</td>
            <td colSpan={2} rowSpan={3} className={styles.authorised}>
              <div>Authorised By:</div>
              <div className={styles.authorisedName}>
                {payrollDetails?.approvedBy}
              </div>
            </td>
          </tr>
          <tr>
            <td>Over time(x2)</td>
            <td className={styles.center}>{money(ot2)}</td>
          </tr>
          <tr>
            <td>Commision</td>
            <td className={styles.center}>{money(commission)}</td>
          </tr> */}
          <tr className={styles.totalRow}>
            <td>Gross Pay</td>
            <td className={styles.center}>
              {money(payrollDetails?.grossSalary)}
            </td>
            <td colSpan={2} rowSpan={9} className={styles.stampCell}>
              {companyDetails?.companyStamp && (
                <img
                  className={styles.stamp}
                  src={`data:image/png;base64,${companyDetails.companyStamp}`}
                  alt="Company Stamp"
                />
              )}
            </td>
          </tr>

          <tr className={styles.spacer}>
            <td colSpan={2}>&nbsp;</td>
          </tr>

          {/* deductions */}
          {payrollDetails?.deductionsList.map(
            (pd, _k) => (
              // Number(pd?.amount) !== Number(0) && (
              <tr key={_k}>
                <td>{pd?.description}</td>
                <td className={styles.center}>{moneyOrNA(pd?.amount)}</td>
              </tr>
            ),

            // <div className={styles.payrollListRow} key={_k}>
            //   <span className={styles.label}>{pd?.description}</span>
            //   <span className={styles.value}>
            //     {Number(pd?.amount).toFixed(2)}
            //   </span>
            // </div>
            // )
          )}
          {/* <tr>
            <td>CPF- Employer</td>
            <td className={styles.center}>{moneyOrNA(cpfEmployer)}</td>
          </tr>
          <tr>
            <td>CPF -Employee</td>
            <td className={styles.center}>{moneyOrNA(cpfEmployee)}</td>
          </tr>
          <tr>
            <td>Agency Fee</td>
            <td className={styles.center}>{moneyOrNA(agencyFee)}</td>
          </tr>
          <tr>
            <td>Advance / Loan</td>
            <td className={styles.center}>{money(advanceLoan)}</td>
          </tr>*/}
          <tr className={`${styles.totalRow} ${styles.italic}`}>
            <td>Total Deductions</td>
            <td className={styles.center}>
              {money(payrollDetails?.totalDeduction)}
            </td>
          </tr>

          <tr className={styles.spacer}>
            <td colSpan={2}>&nbsp;</td>
          </tr>

          <tr className={`${styles.netRow} ${styles.italic}`}>
            <td>Net Pay</td>
            <td className={styles.center}>{money(payrollDetails?.netPay)}</td>
          </tr>
        </tbody>
      </table>

      <p className={styles.footerNote}>
        *THIS IS COMPUTER GENERATED PAY SLIP NO SIGNATURE REQUIRED
      </p>
    </div>
  );
};

TajmahalPayslip.propTypes = {
  payrollDetails: PropTypes.object.isRequired,
  companyDetails: PropTypes.object.isRequired,
};

export default TajmahalPayslip;

// import React from "react";

// import styles from "../TemplatePayslip.module.css";
// import PropTypes from "prop-types";
// import { endOfMonth, format, startOfMonth } from "date-fns";
// import ImageWithBasePath from "core/img/imagewithbasebath";

// const TajmahalPayslip = ({ payrollDetails, companyDetails }) => {
//   const additionRows = payrollDetails?.additionsList?.length + 1;
//   const deductionRows = payrollDetails?.deductionsList?.length || 0;
//   console.log(payrollDetails);
//   return (
//     <div className={styles.payslip}>
//       <div className={styles.header}>
//         {/* <ImageWithBasePath src="assets/img/logo.png" alt="Logo" /> */}
//         <img
//           src={`data:image/png;base64,${companyDetails?.companyLogo}`}
//           alt="Compnay Logo"
//           style={{ width: "110px" }}
//         />
//         <div className={styles["header-text"]}>
//           <h2 className="mb-2">{companyDetails?.companyName.toUpperCase()}</h2>
//           <h6>
//             Payslip for the period of from &nbsp;
//             {format(new Date(payrollDetails?.fromDate), "dd-MM-yyyy")} to&nbsp;
//             {format(new Date(payrollDetails?.toDate), "dd-MM-yyyy")}
//           </h6>
//         </div>
//         <div className={styles["header-date"]}>
//           <h6>Date : {format(new Date(), "dd-MM-yyyy hh:mm:ss a")}</h6>
//         </div>
//       </div>
//       <table className={styles.details}>
//         <tr>
//           <td>Employee No</td>
//           <td>:&nbsp;&nbsp;&nbsp;{payrollDetails?.uniqueID}</td>
//           <td>Designation</td>
//           <td>:&nbsp;&nbsp;&nbsp;{payrollDetails?.designationName}</td>
//         </tr>
//         <tr>
//           <td>Name</td>
//           <td>:&nbsp;&nbsp;&nbsp;{payrollDetails?.fullName}</td>
//           <td>Pay Date</td>
//           <td>
//             :&nbsp;&nbsp;&nbsp;
//             {format(new Date(payrollDetails?.paymentDate), "dd-MM-yyyy")}
//           </td>
//         </tr>
//         <tr>
//           <td>Department</td>
//           <td>:&nbsp;&nbsp;&nbsp;{payrollDetails?.departmentName}</td>

//           <td>DOB / Age</td>
//           <td>:&nbsp;&nbsp;&nbsp;{payrollDetails?.hpno}</td>
//         </tr>

//         <tr>
//           <td>Job Title</td>
//           <td>:&nbsp;&nbsp;&nbsp;{payrollDetails?.jobTitle}</td>
//           <td>FIN / IC Number</td>
//           <td>
//             :&nbsp;&nbsp;&nbsp;
//             {payrollDetails?.finNumber}
//             {payrollDetails?.icno}
//           </td>
//         </tr>

//         <tr>
//           <td>Paymode</td>
//           <td>:&nbsp;&nbsp;&nbsp;{payrollDetails?.paymentMode}</td>
//           <td>Bank Details:</td>
//           <td>
//             :&nbsp;&nbsp;&nbsp;{payrollDetails?.bankname}&nbsp;
//             {payrollDetails?.accountNo || ""}
//           </td>
//         </tr>
//       </table>

//       <div className={styles["wrapper"]}>
//         <div className={styles["table-container"]}>
//           <div className={`${styles["table-header"]} mb-2`}>Earning (SGD)</div>

//           {payrollDetails?.earningsList.map(
//             (pd, _k) =>
//               Number(pd?.amount) !== Number(0) && (
//                 <div className={styles.payrollListRow} key={_k}>
//                   <span className={styles.label}>{pd?.description}</span>
//                   <span className={styles.value}>
//                     {Number(pd?.amount).toFixed(2)}
//                   </span>
//                 </div>
//               )
//           )}
//           <div className={styles["footer"]}>
//             <div className={styles.payrollListRow}>
//               <span className={styles.label}>Gross Total</span>
//               <span className={styles.value}>
//                 {Number(payrollDetails?.grossSalary).toFixed(2)}
//               </span>
//             </div>
//             <div className={styles.payrollListRow}>
//               <span className={styles.label}>OT Amount</span>
//               <span className={styles.value}>
//                 {Number(payrollDetails?.otTotal).toFixed(2)}
//               </span>
//             </div>
//           </div>

//           <div className={styles["footer-2"]}>
//             {/* <div className={styles.payrollListRow}>
//               <span className={styles.label}>Gross Salary</span>
//               <span>{payrollDetails?.grossSalary}</span>
//             </div> */}
//             <div className={styles.payrollListRow}>
//               <span className={styles.label}>Employer CPF</span>
//               <span>{Number(payrollDetails?.employerCpf).toFixed(2)}</span>
//             </div>
//             {/* <div className={styles.payrollListRow}>
//               <span className={styles.label}>YTD Gross Salary</span>
//               <span>695.90</span>
//             </div> */}
//           </div>
//         </div>

//         <div className={styles["table-container"]}>
//           <div className={`${styles["table-header"]} mb-2`}>
//             Deductions (SGD)
//           </div>
//           {payrollDetails?.deductionsList.map(
//             (pd, _k) =>
//               Number(pd?.amount) !== Number(0) && (
//                 <div className={styles.payrollListRow} key={_k}>
//                   <span className={styles.label}>{pd?.description}</span>
//                   <span className={styles.value}>
//                     {Number(pd?.amount).toFixed(2)}
//                   </span>
//                 </div>
//               )
//           )}

//           <div className={styles["footer"]}>
//             <div className={styles.payrollListRow}>
//               <span className={styles.label}>Total Deduction</span>
//               <span className={styles.value}>
//                 {Number(payrollDetails?.totalDeduction).toFixed(2)}
//               </span>
//             </div>
//             <div className={styles.payrollListRow}>
//               <span className={styles.label}>Net Payment</span>
//               <span className={styles.value}>
//                 {Number(payrollDetails?.netPay).toFixed(2)}
//               </span>
//             </div>
//           </div>

//           <div className={styles["footer-2"]}>
//             <div className={styles.payrollListRow}>
//               <span className={styles.label}>&nbsp;</span>
//               <span>&nbsp;</span>
//             </div>
//             {/* <div className={styles.payrollListRow}>
//               <span className={styles.label}>Total Deductions</span>
//               <span>695.90</span>
//             </div> */}
//             {/* <div className={styles.payrollListRow}>
//               <span className={styles.label}>Net Salary</span>
//               <span>{payrollDetails?.netPay}</span>
//             </div> */}
//             {/* <div className={styles.payrollListRow}>
//               <span className={styles.label}>YTD Employee Salary</span>
//               <span>695.90</span>
//             </div> */}
//           </div>
//         </div>
//       </div>

//       <div className={styles.signatureSection}>
//         <div className={styles.label}>
//           Prepared By : {payrollDetails?.reviewedBy}
//         </div>
//         <div className={styles.label}>
//           Approved By : {payrollDetails?.approvedBy}
//         </div>
//         <div className={styles.label}>
//           Received By : {payrollDetails?.fullName}
//         </div>
//         <div className={styles.label}>
//           Employee Signature : _________________________
//         </div>
//       </div>

//       <div className={styles["bottom-text"]}>
//         <p className="text-center">
//           This is computer generated payslip, No Signature is required.
//         </p>
//       </div>
//     </div>
//   );
// };
// TajmahalPayslip.propTypes = {
//   payrollDetails: PropTypes.object.isRequired,
//   companyDetails: PropTypes.object.isRequired,
// };
// export default TajmahalPayslip;
